import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import CheckInButton from "./CheckInButton";
import CameraCapture from "./CameraCapture";
import LocationAccess from "./LocationAccess";
import { db, storage } from "../../firebase/firebase";
import { getDownloadURL, ref, uploadString, uploadBytes } from "firebase/storage";
import {
  addDoc,
  collection,
  getDocs,
  limit,
  query,
  updateDoc,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import TimeSheetEntry from "../TimeSheetEntry/TimeSheetEntry";
import { FiLogIn, FiLogOut, FiCamera, FiSun } from "react-icons/fi";
import { SiGooglesheets } from "react-icons/si";
import { ChakraIcon } from "../ChakraIcon";
import { MdWork, MdHome, MdMeetingRoom, MdComputer } from "react-icons/md";
import CheckoutComingSoon from "../../pages/BurnOut/CheckoutComingSoon";
import { format, startOfWeek, addDays, isWithinInterval, parseISO } from "date-fns";
import LeaveApply from "../LeaveApply/LeaveApply";
import WfhApply from "../WfhApply/WfhApply";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../utils/themeUtils";
import { fetchLeaveEntries } from "../../utils/leaveUtils";
import {
  getCheckInMonthKey,
  getCheckInMonthCol,
  addCheckInRecord,
} from "../../utils/checkInOutUtils";

export type TabType =
  | "Users"
  | "Projects"
  | "Tasks"
  | "TimeSheets"
  | "Progress"
  | "KPI-Metric"
  | "Notifications"
  | "Sentiment"
  | "Burn-Out"
  | "Calendar"
  | "Alert"
  | "TimeSheet Approval"
  | "Leave Approval"
  | "Approvals"
  | "profile"
  | "LogPanel"
  | "ManagerBooking"
  | "SeatBooking"
  | "WorkGroup"
  | "Overview"
  | "Departments"
  | "Leave Management"
  | "Team Chats"
  | "My Chats"
  | "SystemLogs"
  | "Help"
  | "Check-Ins"
  | "System Settings";


// Helper to get address from lat/lon using Nominatim
async function getAddressFromLatLng(lat: number, lon: number): Promise<string> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1&accept-language=en`
    );
    const data = await res.json();

    if (data && data.address) {
      const a = data.address;
      const parts = [
        a.house_number && a.road ? `${a.house_number} ${a.road}` : a.road,
        a.suburb,
        a.city || a.town || a.village,
        a.state,
        a.postcode,
        a.country,
      ].filter(Boolean);
      if (parts.length) return parts.join(", ");
    }

    return data.display_name || "Unknown location";
  } catch {
    return "Unknown location";
  }
}

// Helper to get coordinates from address using Nominatim
async function getLatLngFromAddress(address: string): Promise<{ lat: number; lon: number } | null> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
        address
      )}&format=json&limit=1`,
      { headers: { 'User-Agent': 'ZenSight/1.0' } }
    );
    const data = await res.json();
    if (data && data.length > 0) {
      return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
    }
    return null;
  } catch (err) {
    console.warn("Geocoding failed for address:", address, err);
    return null;
  }
}

// Distance calculation using Haversine formula
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371e3; // metres
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // in metres
}

type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  assignedChannels?: Record<string, string[]>;
  assignedProjects?: string[];
  assignedTasks?: {
    id: string;
    name: string;
    taskId: string;
    channelId: string;
    projectId: string;
  }[];
  organizationId: string;
  address1?: string;
  address2?: string;
};

interface CheckInMainProps {
  currentUser: User | null;
  bg: string;
  textColor: string;
  organizationId: string;
  setActiveTab?: React.Dispatch<React.SetStateAction<TabType>>;
}

type Location = { latitude: number; longitude: number };

type Step =
  | "idle"
  | "checkin_camera"
  | "checkin_location"
  | "checked_in"
  | "checkout_camera"
  | "checkout_location"
  | "task_update"
  | "summary";

const getLocalKey = (uid: string) => `zensight_checkin_data_${uid}`;
const getHistoryKey = (uid: string) => `zensight_checkin_history_${uid}`;

function isToday(date: Date) {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

const ModalPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (typeof document === "undefined") return <>{children}</> as any;
  return createPortal(children as any, document.body);
};

interface CheckInImageProps {
  src: string | null;
  alt: string;
  className?: string;
  isDark: boolean;
}

const CheckInImage: React.FC<CheckInImageProps> = ({ src, alt, className = "", isDark }) => {
  const [hasError, setHasError] = useState(false);

  if (!src) return null;

  const isLocalMobile = src.startsWith("file:///");

  let filename = "";
  if (isLocalMobile) {
    try {
      const parts = src.split("/");
      filename = parts[parts.length - 1];
    } catch {
      filename = "image.jpg";
    }
  }

  if (isLocalMobile || hasError) {
    return (
      <div
        className={`${className} flex flex-col items-center justify-center p-4 text-center rounded-lg border transition-all duration-300 ${isDark
          ? "bg-[#14161B] border-[#2E2F33] text-gray-300"
          : "bg-gray-50 border-gray-200 text-gray-600"
          }`}
      >
        <div
          className={`flex items-center justify-center p-3 rounded-full mb-2 ${isDark ? "bg-[#B59143]/10 text-[#FBD282]" : "bg-[#FFF7E6] text-[#B59143]"
            }`}
        >
          <ChakraIcon as={FiCamera} boxSize={6} />
        </div>
        <span className="text-xs font-bold uppercase tracking-wider block mb-1 text-[#B59143]">
          {isLocalMobile ? "Mobile Capture" : "Image Unavailable"}
        </span>
        <span className="text-[11px] leading-tight opacity-80 block max-w-xs mx-auto">
          {isLocalMobile
            ? "Photo Unavailable."
            : "Failed to load check-in photo."}
        </span>

      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setHasError(true)}
    />
  );
};

const formatDuration = (ms: number): string => {
  const totalMin = Math.round(ms / 60000);
  const hrs = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  return `${hrs} hr ${mins} min`;
};


const CheckInMain: React.FC<CheckInMainProps> = ({
  currentUser,
  bg,
  textColor,
  organizationId,
  setActiveTab,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  // Ensure overlay covers viewport regardless of transformed ancestors
  // This effect is defined after step is declared (below) but referenced via function hoisting
  const [step, setStep] = useState<Step>("idle");
  // Ref so that findActiveCheckIn can read the current step synchronously
  // without being added to the useEffect dependency array (which would restart the interval).
  const stepRef = React.useRef<Step>("idle");
  const setStepSafe = (s: Step) => { stepRef.current = s; setStep(s); };
  const [checkInPhoto, setCheckInPhoto] = useState<string | null>(null);
  const [checkInLocation, setCheckInLocation] = useState<Location | null>(null);
  const [checkInAddress, setCheckInAddress] = useState<string | null>(null);
  const [checkInTime, setCheckInTime] = useState<Date | null>(null);

  const [checkOutPhoto, setCheckOutPhoto] = useState<string | null>(null);
  const [checkOutLocation, setCheckOutLocation] = useState<Location | null>(
    null
  );
  const [checkOutAddress, setCheckOutAddress] = useState<string | null>(null);
  const [checkOutTime, setCheckOutTime] = useState<Date | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [localTab, setLocalTab] = useState<"checkin" | "timesheet" | "leave" | "wfh">("checkin");

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const tabListRef = useRef<HTMLDivElement | null>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number }>({ left: 0, width: 0 });

  const measureTab = useCallback(() => {
    const active = tabRefs.current[localTab];
    const list = tabListRef.current;
    if (!active || !list) return;
    const listRect = list.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    setIndicator({
      left: activeRect.left - listRect.left,
      width: activeRect.width,
    });
  }, [localTab]);

  useEffect(() => {
    measureTab();
    window.addEventListener("resize", measureTab);
    return () => window.removeEventListener("resize", measureTab);
  }, [measureTab]);

  // --- NEW: Task Update States ---
  const [showTaskUpdateModal, setShowTaskUpdateModal] = useState(false);
  const [taskUpdateText, setTaskUpdateText] = useState("");
  const [pendingCheckoutData, setPendingCheckoutData] = useState<{
    address: string;
    photoUrl: string;
    now: Date;
    loc: Location;
    checkOutLocationStatus: string;
  } | null>(null);

  // --- NEW: Work Mode States ---
  const [showWorkModeModal, setShowWorkModeModal] = useState(false);
  const [selectedWorkMode, setSelectedWorkMode] = useState<"Office" | "Home">("Office");
  const [pendingCheckInData, setPendingCheckInData] = useState<{
    address: string;
    photoUrl: string;
    now: Date;
    loc: Location;
    locationStatus: string;
  } | null>(null);



  // Utility to display only street name and district from full Nominatim address
  const shortenAddress = (addr: string) => {
    if (!addr) return "";
    const parts = addr.split(",");
    if (parts.length >= 3) {
      return `${parts[0].trim()}, ${parts[2].trim()}`;
    }
    if (parts.length >= 2) {
      return `${parts[0].trim()}, ${parts[1].trim()}`;
    }
    return addr;
  };

  const [isCheckInSubmitting, setIsCheckInSubmitting] = useState(false);
  const [isCheckOutSubmitting, setIsCheckOutSubmitting] = useState(false);
  const [checkInStatus, setCheckInStatus] = useState("");

  useEffect(() => {
    const hasModal = ["checkin_camera", "checkin_location", "checkout_camera", "checkout_location"].includes(step);
    if (typeof document !== 'undefined') {
      if (hasModal) document.body.classList.add('overflow-hidden');
      else document.body.classList.remove('overflow-hidden');
    }
    return () => {
      if (typeof document !== 'undefined') document.body.classList.remove('overflow-hidden');
    };
  }, [step]);



  // Header title/subtitle based on local tab
  const headerTitle =
    localTab === "checkin" ? "Check-In / Check-Out" :
      localTab === "timesheet" ? "Timesheet Entry" :
        localTab === "wfh" ? "Remote Work" :
          "Leave Management";
  const headerSubtitle =
    localTab === "checkin"
      ? "Easily check in and out, capture your location and photo."
      : localTab === "timesheet"
        ? ""
        : localTab === "wfh"
          ? "Apply for remote work and track your requests."
          : "Apply for leave and track your leave requests.";

  // Update browser tab title based on active section
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = `ZenSight · ${headerTitle}`;
    }
  }, [localTab]);

  const uploadImageAndGetUrl = async (
    base64Image: string,
    path: string
  ): Promise<string> => {
    const imageRef = ref(storage, path);
    // Convert base64 to Blob for more efficient binary upload (faster than base64 string)
    const res = await fetch(base64Image);
    const blob = await res.blob();
    await uploadBytes(imageRef, blob);
    return await getDownloadURL(imageRef);
  };

  // Restore check-in state from localStorage on mount (per-user)
  useEffect(() => {
    if (!currentUser?.id) return;

    const key = getLocalKey(currentUser.id);
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (
          data &&
          data.checkInAddress &&
          data.checkInTime &&
          !data.checkOutTime
        ) {
          // Note: Photo is usually fetched from Firestore for persistence if possible, 
          // but we restore local address/time to keep the UI snappy.
          setCheckInAddress(data.checkInAddress);
          setCheckInTime(new Date(data.checkInTime));
          setStepSafe("checked_in");
        }
      } catch { }
    }
    // Load today's history
    const fetchTodayHistory = async () => {
      if (!currentUser) return;

      const todayDateStr = new Date().toISOString().split("T")[0]; // "2025-07-09"
      const monthKey = getCheckInMonthKey();
      const userRef = getCheckInMonthCol(currentUser.organizationId, currentUser.id, monthKey);

      const snapshot = await getDocs(userRef);
      const todayRecords: any[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.checkInTime && data.checkInTime.startsWith(todayDateStr)) {
          todayRecords.push(data);
        }
      });

      todayRecords.sort(
        (a, b) =>
          new Date(b.checkInTime).getTime() - new Date(a.checkInTime).getTime()
      );
      setHistory(todayRecords);
    };

    fetchTodayHistory();

    // Check if there is an active (not checked-out) record in Firestore
    const findActiveCheckIn = async () => {
      if (!currentUser) return;
      // Do not interfere while the user is actively mid-check-in or mid-checkout flow.
      // Overwriting step during camera/location/task steps would close the active modal.
      const ACTIVE_FLOW_STEPS: Step[] = [
        "checkin_camera", "checkin_location",
        "checkout_camera", "checkout_location",
        "task_update",
      ];
      if (ACTIVE_FLOW_STEPS.includes(stepRef.current)) return;
      try {
        const now = new Date();
        const monthKeys = [
          getCheckInMonthKey(now),
          getCheckInMonthKey(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
          getCheckInMonthKey(new Date(now.getFullYear(), now.getMonth() - 2, 1)),
        ];
        let foundActive = false;
        for (const mk of monthKeys) {
          if (foundActive) break;
          const qSnap = query(
            getCheckInMonthCol(currentUser.organizationId, currentUser.id, mk),
            where("isCheckedOut", "==", false),
            limit(1)
          );
          const snapshot = await getDocs(qSnap);
          if (!snapshot.empty) {
            foundActive = true;
            const docSnap = snapshot.docs[0];
            const docData = docSnap.data();

            const checkInTimeStr: string = docData.checkInTime;
            const checkInDate = new Date(checkInTimeStr);

            // Use local date comparison to handle timezone differences
            const checkInDateLocalStr = format(checkInDate, "yyyy-MM-dd");
            const todayLocalStr = format(new Date(), "yyyy-MM-dd");
            const isLateNight = new Date().getHours() === 23 && new Date().getMinutes() >= 59;

            if (todayLocalStr > checkInDateLocalStr || (todayLocalStr === checkInDateLocalStr && isLateNight)) {
              // Auto-checkout at 11:59:00 PM LOCAL time of the check-in day.
              // Build the timestamp from the local date string to avoid UTC/IST midnight-shift bugs.
              const autoCheckoutDate = new Date(`${checkInDateLocalStr}T23:59:00`);
              const autoCheckoutISO = autoCheckoutDate.toISOString();
              const durationMs = Math.max(0, autoCheckoutDate.getTime() - checkInDate.getTime());

              const mins = Math.floor(durationMs / 60000);
              const hrs = Math.floor(mins / 60);
              const rem = mins % 60;
              const duration = hrs ? `${hrs} hr ${rem} min` : `${rem} min`;

              // --- Migrate legacy base64 check-in photo before writing ---
              // If checkInPhotoUri is still a raw base64 string, upload it to Storage first.
              // Otherwise the updateDoc write will also hit the 1MB Firestore limit.
              let autoCheckoutPhotoUri = docData.checkInPhotoUri || "";
              if (autoCheckoutPhotoUri.startsWith("data:")) {
                try {
                  const migratePath = `checkins/${currentUser.organizationId}/${currentUser.id}/${Date.now()}_checkin_migrated.png`;
                  autoCheckoutPhotoUri = await uploadImageAndGetUrl(autoCheckoutPhotoUri, migratePath);
                } catch (uploadErr) {
                  console.warn("Auto-checkout: could not migrate check-in photo, clearing URI.", uploadErr);
                  autoCheckoutPhotoUri = "";
                }
              }

              await updateDoc(docSnap.ref, {
                checkInPhotoUri: autoCheckoutPhotoUri,   // overwrite bloated field if it was base64
                isCheckedOut: true,
                checkOutTime: autoCheckoutISO,
                checkOutAddress: "autocheckout",
                checkOutPhotoUri: autoCheckoutPhotoUri,  // reuse migrated check-in photo
                duration,
                taskUpdate: "Auto-checkout (No update provided)",
                markedAsAbsent: true,
              });

              // Clear local state and localStorage after auto-checkout
              const key = getLocalKey(currentUser.id);
              localStorage.removeItem(key);
              setStepSafe("idle");
              setShowTaskUpdateModal(false);
              setPendingCheckoutData(null);
              setTaskUpdateText("");
              return;
            }

            // Still same day; restore state
            setCheckInPhoto(docData.checkInPhotoUri || null);
            setCheckInAddress(docData.checkInAddress || null);
            setCheckInTime(
              docData.checkInTime ? new Date(docData.checkInTime) : null
            );
            setCheckInLocation(null);
            setStepSafe("checked_in");
          }
        }
        // If no active check-in found across all months
        if (!foundActive) {
          // If no active check-in in Firestore, but UI might be in checked_in state due to localStorage
          if (stepRef.current === "checked_in") {
            const key = getLocalKey(currentUser.id);
            localStorage.removeItem(key);
            setStepSafe("idle");
          }
        }
      } catch (err) {
        console.warn("Unable to verify active check-in:", err);
      }
    };

    findActiveCheckIn();

    // Check every 5 minutes if auto-checkout is needed
    const interval = setInterval(findActiveCheckIn, 5 * 60 * 1000);

    // Also check when window regains focus
    window.addEventListener("focus", findActiveCheckIn);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", findActiveCheckIn);
    };
  }, [currentUser]);

  // Save check-in state to localStorage after check-in (per-user)
  useEffect(() => {
    if (!currentUser?.id) return;
    const key = getLocalKey(currentUser.id);

    if (
      step === "checked_in" &&
      checkInAddress &&
      checkInTime
    ) {
      // Store minimal data to avoid exceeding localStorage quota
      const payload = {
        checkInAddress,
        checkInTime,
      } as const;
      try {
        localStorage.setItem(key, JSON.stringify(payload));
      } catch (err) {
        // Silent fail if quota exceeded; the app will still function
        console.warn("Unable to persist check-in state:", err);
      }
    }
    if (step === "summary") {
      localStorage.removeItem(key);
    }
  }, [step, checkInAddress, checkInTime, currentUser?.id]);

  // Print all relevant data in console



  const handleCheckIn = async () => {
    setStepSafe("checkin_camera");
    setError(null);
  };

  const handleCheckInPhoto = (photoDataUrl: string) => {
    setCheckInPhoto(photoDataUrl);
    setStepSafe("checkin_location");
  };

  const handleCheckInLocation = async (
    loc: Location,
    locationAddress?: string
  ) => {
    if (isCheckInSubmitting) return; // 👈 Prevent double-fire
    setIsCheckInSubmitting(true);
    setCheckInStatus("Detecting address...");
    try {
      setCheckInLocation(loc);
      setLoadingAddress(true);

      const address =
        locationAddress ||
        (await getAddressFromLatLng(loc.latitude, loc.longitude));
      setCheckInAddress(address);

      const now = new Date();
      setCheckInTime(now);

      if (!currentUser || !checkInPhoto) {
        setError("User info or photo missing.");
        setIsCheckInSubmitting(false);
        return;
      }

      const photoPath = `checkins/${currentUser.organizationId}/${currentUser.id}/${Date.now()}_checkin.png`;
      setCheckInStatus("Uploading photo...");
      const photoUrl = await uploadImageAndGetUrl(checkInPhoto, photoPath);

      // Verify location against Address 1 & Address 2
      let locationStatus = "not matched";
      const userAddresses = [currentUser.address1, currentUser.address2].filter(
        (addr): addr is string => !!addr && addr.trim() !== ""
      );

      if (userAddresses.length === 0) {
        console.log("📍 [Check-In] No registered addresses found. Defaulting to 'matched'.");
        locationStatus = "matched"; // Default if no address is set
      } else {
        setCheckInStatus("Verifying location...");
        console.log(`📍 [Check-In] Verifying against ${userAddresses.length} addresses in parallel...`);
        const matches = await Promise.all(userAddresses.map(async (addr) => {
          const coords = await getLatLngFromAddress(addr);
          if (coords) {
            const dist = calculateDistance(
              loc.latitude,
              loc.longitude,
              coords.lat,
              coords.lon
            );
            console.log(`🏠 Address: "${addr}" | Distance: ${dist.toFixed(2)}m`);
            return dist <= 1000;
          }
          return false;
        }));

        if (matches.includes(true)) {
          console.log("✅ Match found!");
          locationStatus = "matched";
        } else {
          console.log("❌ No match found for any address.");
        }
      }

      const weekKey = format(startOfWeek(now, { weekStartsOn: 1 }), "yyyy-MM-dd");
      const dateKey = format(now, "yyyy-MM-dd");

      const plannerDoc = await getDoc(doc(db, `organization/${currentUser.organizationId}/workerPlanner/${currentUser.id}/weeks`, weekKey));
      let detectedWorkMode: "Office" | "Home" = "Office"; // Default fallback

      if (plannerDoc.exists()) {
        const weekPlan = plannerDoc.data();
        if (weekPlan && weekPlan[dateKey] && weekPlan[dateKey].workMode) {
          detectedWorkMode = weekPlan[dateKey].workMode as "Office" | "Home";
        }
      }

      setPendingCheckInData({
        address,
        photoUrl,
        now,
        loc,
        locationStatus,
      });
      setSelectedWorkMode(detectedWorkMode);
      setCheckInStatus("");
      setShowWorkModeModal(true);
      setIsCheckInSubmitting(false); // Modal is now open, waiting for user
    } catch (err) {
      console.error("Check-in failed:", err);
      setError("Something went wrong during check-in.");
      setIsCheckInSubmitting(false);
    } finally {
      setLoadingAddress(false);
    }
  };

  const handleWorkModeSubmit = async () => {
    if (!pendingCheckInData || !currentUser) return;
    setIsCheckInSubmitting(true);
    try {
      const { address, photoUrl, now, loc, locationStatus } = pendingCheckInData;

      await addCheckInRecord(
        currentUser.organizationId,
        currentUser.id,
        {
          userId: currentUser.id,
          checkInTime: now.toISOString(),
          checkInAddress: address,
          checkInPhotoUri: photoUrl,
          checkInLatitude: loc.latitude,
          checkInLongitude: loc.longitude,
          locationStatus: locationStatus || "",
          workMode: selectedWorkMode,
          createdAt: new Date().toISOString(),
        }
      );

      setCheckInPhoto(photoUrl);
      setCheckInAddress(address);
      setCheckInTime(now);
      setStepSafe("checked_in");
      setShowWorkModeModal(false);
      setPendingCheckInData(null);
    } catch (err) {
      console.error("Check-in finalize failed:", err);
      setError("Failed to finalize check-in.");
    } finally {
      setIsCheckInSubmitting(false);
    }
  };

  const handleWorkModeCancel = () => {
    setShowWorkModeModal(false);
    setPendingCheckInData(null);
    setIsCheckInSubmitting(false);
    setStepSafe("idle");
  };

  // Check-Out Flow
  const handleCheckOut = async () => {
    setStepSafe("checkout_camera");
    setError(null);
  };

  const handleCheckOutPhoto = (photoDataUrl: string) => {
    setCheckOutPhoto(photoDataUrl);
    setStepSafe("checkout_location");
  };

  const handleCheckOutLocation = async (
    loc: Location,
    locationAddress?: string
  ) => {
    if (isCheckOutSubmitting) return; // Prevent double-fire
    setIsCheckOutSubmitting(true);
    try {
      setCheckOutLocation(loc);
      setLoadingAddress(true);

      const address =
        locationAddress ||
        (await getAddressFromLatLng(loc.latitude, loc.longitude));
      setCheckOutAddress(address);
      const now = new Date();
      setCheckOutTime(now);

      if (!currentUser || !checkOutPhoto || !checkInTime) {
        setError("Missing data for checkout.");
        setIsCheckOutSubmitting(false);
        return;
      }

      const photoPath = `checkouts/${currentUser.organizationId}/${currentUser.id}/${Date.now()}_checkout.png`;
      const photoUrl = await uploadImageAndGetUrl(checkOutPhoto, photoPath);

      const duration = formatDuration(now.getTime() - checkInTime.getTime());

      // Find active check-in across current + previous months
      const scanDate = new Date();
      const monthKeysToScan = [
        getCheckInMonthKey(scanDate),
        getCheckInMonthKey(new Date(scanDate.getFullYear(), scanDate.getMonth() - 1, 1)),
        getCheckInMonthKey(new Date(scanDate.getFullYear(), scanDate.getMonth() - 2, 1)),
      ];
      let activeRef: any = null;
      let activeMonthKey = "";
      for (const mk of monthKeysToScan) {
        const q = query(
          getCheckInMonthCol(currentUser.organizationId, currentUser.id, mk),
          where("isCheckedOut", "==", false),
          limit(1)
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          activeRef = snapshot.docs[0].ref;
          activeMonthKey = mk;
          break;
        }
      }

      if (activeRef) {
        const docRef = activeRef;

        // Verify location against Address 1 & Address 2
        let checkOutLocationStatus = "not matched";
        const userAddresses = [currentUser.address1, currentUser.address2].filter(
          (addr): addr is string => !!addr && addr.trim() !== ""
        );

        if (userAddresses.length === 0) {
          console.log("📍 [Check-Out] No registered addresses found. Defaulting to 'matched'.");
          checkOutLocationStatus = "matched"; // Default if no address is set
        } else {
          console.log(`📍 [Check-Out] Verifying against ${userAddresses.length} addresses...`);
          for (const addr of userAddresses) {
            const coords = await getLatLngFromAddress(addr);
            if (coords) {
              const dist = calculateDistance(
                loc.latitude,
                loc.longitude,
                coords.lat,
                coords.lon
              );
              console.log(`🏠 Address: "${addr}"`);
              console.log(`🎯 Target: (${coords.lat}, ${coords.lon}) | Current: (${loc.latitude}, ${loc.longitude})`);
              console.log(`📏 Distance: ${dist.toFixed(2)}m (Threshold: 1000m)`);

              if (dist <= 1000) {
                console.log("✅ Match found!");
                checkOutLocationStatus = "matched";
                break;
              } else {
                console.log("❌ No match for this address.");
              }
            } else {
              console.warn(`⚠️ Could not geocode address: "${addr}"`);
            }
          }
        }

        // Store pending data and show Task Update Modal
        setPendingCheckoutData({
          address,
          photoUrl,
          now,
          loc,
          checkOutLocationStatus
        });
        setStepSafe("task_update");
        setShowTaskUpdateModal(true);
      } else {
        // No active check-in found; session might have been auto-checked out
        setError("No active check-in found. Your session may have been automatically checked out.");
        const key = getLocalKey(currentUser.id);
        localStorage.removeItem(key);
        setStepSafe("idle");
      }
    } catch (err) {
      console.error("Check-out preparation failed:", err);
      setError("Something went wrong preparing your checkout.");
    } finally {
      setLoadingAddress(false);
      setIsCheckOutSubmitting(false);
    }
  };

  const handleTaskUpdateSubmit = async () => {
    if (!taskUpdateText.trim()) {
      setError("Please provide a task update.");
      return;
    }

    if (!pendingCheckoutData || !currentUser || !checkInTime) return;

    setIsCheckOutSubmitting(true);
    try {
      const { address, photoUrl, now, loc, checkOutLocationStatus } = pendingCheckoutData;
      const duration = formatDuration(now.getTime() - checkInTime.getTime());

      // Find active check-in across current + previous months
      const scanDate2 = new Date();
      const monthKeysScan2 = [
        getCheckInMonthKey(scanDate2),
        getCheckInMonthKey(new Date(scanDate2.getFullYear(), scanDate2.getMonth() - 1, 1)),
        getCheckInMonthKey(new Date(scanDate2.getFullYear(), scanDate2.getMonth() - 2, 1)),
      ];
      let activeRef2: any = null;
      let activeMonthKey2 = "";
      for (const mk of monthKeysScan2) {
        const q = query(
          getCheckInMonthCol(currentUser.organizationId, currentUser.id, mk),
          where("isCheckedOut", "==", false),
          limit(1)
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          activeRef2 = snapshot.docs[0].ref;
          activeMonthKey2 = mk;
          break;
        }
      }

      if (activeRef2) {
        const docRef = activeRef2;
        const existingData = (await getDoc(docRef)).data();

        // --- Migrate legacy base64 check-in photo to Storage ---
        // If the stored checkInPhotoUri is a raw base64 data URL (not an https:// URL),
        // upload it now so the document size is reduced before we add checkout fields.
        let resolvedCheckInPhotoUri = (existingData as any)?.checkInPhotoUri || "";
        if (resolvedCheckInPhotoUri.startsWith("data:")) {
          try {
            const migratePath = `checkins/${currentUser.organizationId}/${currentUser.id}/${Date.now()}_checkin_migrated.png`;
            resolvedCheckInPhotoUri = await uploadImageAndGetUrl(resolvedCheckInPhotoUri, migratePath);
          } catch (uploadErr) {
            console.warn("Could not migrate check-in photo to Storage; clearing to prevent size error.", uploadErr);
            resolvedCheckInPhotoUri = ""; // Clear rather than fail the whole checkout
          }
        }

        await updateDoc(docRef, {
          // Overwrite the (potentially bloated) check-in photo with the migrated Storage URL
          checkInPhotoUri: resolvedCheckInPhotoUri,
          isCheckedOut: true,
          checkOutTime: now.toISOString(),
          checkOutAddress: address,
          checkOutPhotoUri: photoUrl,
          duration,
          checkOutLatitude: loc.latitude,
          checkOutLongitude: loc.longitude,
          checkOutLocationStatus,
          taskUpdate: taskUpdateText.trim(),
        });

        // Set state for summary view
        setCheckOutAddress(address);
        setCheckOutTime(now);
        setCheckOutLocation(loc);
        setCheckOutPhoto(photoUrl);

        setShowTaskUpdateModal(false);
        setTaskUpdateText("");
        setPendingCheckoutData(null);
        setStepSafe("summary");
      } else {
        // Handle case where record is gone (already checked out)
        setError("Unable to find your active check-in session.");
        const key = getLocalKey(currentUser.id);
        localStorage.removeItem(key);
        setStepSafe("idle");
        setShowTaskUpdateModal(false);
      }
    } catch (err) {
      console.error("Final checkout failed:", err);
      setError("Something went wrong finalizing your checkout.");
    } finally {
      setIsCheckOutSubmitting(false);
    }
  };

  const handleLocationError = (err: string) => {
    setError(err);
    setStepSafe("idle");
    setCheckInPhoto(null);
    setCheckInLocation(null);
    setCheckInAddress(null);
    setCheckInTime(null);
    setCheckOutPhoto(null);
    setCheckOutLocation(null);
    setCheckOutAddress(null);
    setCheckOutTime(null);
  };

  const handleCancel = () => {
    const currentStep = stepRef.current;
    if (
      currentStep === "checkout_camera" ||
      currentStep === "checkout_location" ||
      currentStep === "task_update"
    ) {
      setStepSafe("checked_in");
      setCheckOutPhoto(null);
      setCheckOutLocation(null);
      setCheckOutAddress(null);
      setCheckOutTime(null);
      setPendingCheckoutData(null);
      setError(null);
    } else {
      setStepSafe("idle");
      setCheckInPhoto(null);
      setCheckInLocation(null);
      setCheckInAddress(null);
      setCheckInTime(null);
      setCheckOutPhoto(null);
      setCheckOutLocation(null);
      setCheckOutAddress(null);
      setCheckOutTime(null);
      setPendingCheckoutData(null);
      setError(null);
    }
  };

  // Duration calculation
  let durationStr = "";
  if (checkInTime && checkOutTime) {
    const diffMs = checkOutTime.getTime() - checkInTime.getTime();
    durationStr = formatDuration(diffMs);
  }

  // Format date/time
  const formatDateTime = (date: Date | null) => {
    if (!date) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const formattedHours = String(hours).padStart(2, "0");

    return `${day}/${month}/${year}, ${formattedHours}:${minutes}:${seconds} ${ampm}`;
  };

  // Save to history after check-out
  const saveToHistory = () => {
    if (
      checkInPhoto &&
      checkInLocation &&
      checkInAddress &&
      checkInTime &&
      checkOutPhoto &&
      checkOutLocation &&
      checkOutAddress &&
      checkOutTime
    ) {
      const recordFull = {
        checkInPhoto,
        checkInLocation,
        checkInAddress,
        checkInTime: checkInTime.toISOString(),
        checkOutPhoto,
        checkOutLocation,
        checkOutAddress,
        checkOutTime: checkOutTime.toISOString(),
        duration: durationStr,
      } as const;

      // Push/replace into component state (avoid duplicates by check-in timestamp)
      setHistory((prev) => {
        const filtered = prev.filter(
          (r) => r.checkInTime !== recordFull.checkInTime
        );
        const combined = [...filtered, recordFull];
        combined.sort(
          (a, b) =>
            new Date(b.checkInTime).getTime() -
            new Date(a.checkInTime).getTime()
        );
        return combined;
      });

      // Persist a light-weight version (without base64 photos) to avoid quota issues
      const recordSlim = {
        checkInTime: recordFull.checkInTime,
        checkOutTime: recordFull.checkOutTime,
        duration: recordFull.duration,
        checkInAddress: recordFull.checkInAddress,
        checkOutAddress: recordFull.checkOutAddress,
      } as const;

      try {
        if (!currentUser?.id) return;
        const key = getHistoryKey(currentUser.id);
        let allSlim: any[] = [];
        const raw = localStorage.getItem(key);
        if (raw) allSlim = JSON.parse(raw);
        allSlim.push(recordSlim);
        // keep only last 50 records
        if (allSlim.length > 50) allSlim = allSlim.slice(-50);
        localStorage.setItem(key, JSON.stringify(allSlim));
      } catch (err) {
        console.warn(
          "Unable to persist history (likely storage quota reached):",
          err
        );
      }
    }
  };

  // On summary step, save to history (only once)
  useEffect(() => {
    if (step === "summary") {
      saveToHistory();
    }
    // eslint-disable-next-line
  }, [step]);

  // Add custom slow spin animation if not present
  const style = document.createElement("style");
  style.innerHTML = `
  @keyframes spin-slow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
  .animate-spin-slow { animation: spin-slow 3s linear infinite; }
  `;
  if (
    typeof window !== "undefined" &&
    !document.head.querySelector("style[data-spin-slow]")
  ) {
    style.setAttribute("data-spin-slow", "");
    document.head.appendChild(style);
  }

  return (
    <div className={`py-1 px-2 md:px-2 ${isDark ? theme.background : "bg-white"}`}>
      {/* Full-width Gradient Bar */}
      <div className="w-full shadow-sm">
        <div className={`relative flex flex-col md:flex-row justify-between items-center p-4 rounded-md w-full ${theme.headerGradient} border ${theme.border}`}>
          {isDark && (
            <>
              <span className={theme.headerOverlayGlow} />
              <span className={theme.headerOverlayTopLine} />
              <span className={theme.headerOverlayBottomGlow} />
              <span className={theme.headerOverlaySweep} />
            </>
          )}
          <div className="flex-1 w-full md:w-auto text-center md:text-left mb-4 md:mb-0">
            <span className={`text-[22px] font-bold tracking-tight ${isDark ? theme.text : "text-gray-900"}`}>
              {headerTitle}
            </span>
            <div className="flex items-center justify-center md:justify-start gap-2 mt-1">
              <span className={`text-xs ${theme.textMuted}`}>
                {headerSubtitle}
              </span>
            </div>
          </div>
          <div className="w-full md:w-auto flex justify-center md:justify-end">
            <div
              ref={tabListRef}
              role="tablist"
              aria-label="Section selector"
              className={`relative ${isDark ? theme.card : "bg-white/70"} backdrop-blur border ${theme.border} rounded-full p-1 shadow-sm flex w-full max-w-lg`}
            >
              <span
                className={`absolute top-1 bottom-1 rounded-full ${isDark ? "bg-[#2A3441] border-[#2E2F33]" : "bg-[#FFF7E6] border border-[#E8C16C]"} shadow-sm transition-all duration-300`}
                style={{ left: indicator.left, width: indicator.width }}
                aria-hidden="true"
              />
              <button
                ref={(el) => { tabRefs.current["checkin"] = el; }}
                role="tab"
                aria-selected={localTab === "checkin"}
                onClick={() => setLocalTab("checkin")}
                className={`relative z-10 flex-1 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors focus:outline-none ${localTab === "checkin"
                  ? isDark ? theme.text : "text-gray-900"
                  : `${theme.textMuted} hover:text-[#B59143]`
                  }`}
              >
                Check-In
              </button>
              <button
                ref={(el) => { tabRefs.current["timesheet"] = el; }}
                role="tab"
                aria-selected={localTab === "timesheet"}
                onClick={() => setLocalTab("timesheet")}
                className={`relative z-10 flex-1 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors focus:outline-none ${localTab === "timesheet"
                  ? isDark ? theme.text : "text-gray-900"
                  : `${theme.textMuted} hover:text-[#B59143]`
                  }`}
              >
                Timesheet
              </button>
              <button
                ref={(el) => { tabRefs.current["leave"] = el; }}
                role="tab"
                aria-selected={localTab === "leave"}
                onClick={() => setLocalTab("leave")}
                className={`relative z-10 flex-1 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors focus:outline-none ${localTab === "leave"
                  ? isDark ? theme.text : "text-gray-900"
                  : `${theme.textMuted} hover:text-[#B59143]`
                  }`}
              >
                Leave
              </button>
              <button
                ref={(el) => { tabRefs.current["wfh"] = el; }}
                role="tab"
                aria-selected={localTab === "wfh"}
                onClick={() => setLocalTab("wfh")}
                className={`relative z-10 flex-1 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors focus:outline-none ${localTab === "wfh"
                  ? isDark ? theme.text : "text-gray-900"
                  : `${theme.textMuted} hover:text-[#B59143]`
                  }`}
              >
                Remote Work
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Main Card Content - now truly full width */}
      <div className="w-full mt-1 px-0">
        <div className={`${theme.card} rounded-lg p-4 w-full `}>
          {localTab === "checkin" ? (
            <>
              {/* --- Begin Check-In/Check-Out UI --- */}
              <>
                {(step === "idle" || step === "summary") && (
                  <div className={`w-full flex flex-row items-center justify-between gap-3 py-5 px-6 rounded-2xl animate-fade-in transition-shadow duration-200 hover:shadow-sm relative group ${isDark ? "bg-[#14161B] border border-[#2E2F33]" : "bg-gradient-to-r from-white via-gray-50 to-[#FFF7E6]"}`}>
                    {/* Left accent bar */}
                    <div className={`absolute left-0 top-2 bottom-4 w-1 rounded-full ${isDark ? "bg-[#B59143]" : "bg-gradient-to-b from-[#FBD282] to-[#B59143]"} opacity-60 group-hover:opacity-80 transition-opacity`}></div>
                    <div className="flex flex-col justify-center ml-4">
                      <h2 className={`text-md font-semibold mb-1 ${isDark ? theme.text : "text-gray-800"}`}>
                        {step === "summary" ? "All set! You can start a new check-in" : "Start your day by checking in"}
                      </h2>
                      <p className={`${theme.textMuted} text-xs`}>
                        {step === "summary" ? "Your last session is saved. Begin a new one when you're ready." : "Capture your presence and begin your productive day!"}
                      </p>
                    </div>
                    <div className="flex justify-end w-full md:w-auto animate-slide-in-right">
                      <button
                        onClick={handleCheckIn}
                        className={`flex items-center gap-2 px-6 py-2 rounded-full text-white font-bold text-base shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1 focus:outline-none border-0 ${theme.primaryButton}`}
                        aria-label="Check In"
                      >
                        <span className="transition-transform duration-200 group-hover:translate-x-1">
                          <ChakraIcon
                            as={FiLogIn}
                            boxSize={5}
                            className="text-white"
                          />
                        </span>
                        <span>Check In</span>
                      </button>
                    </div>
                  </div>
                )}
                {step === "checkin_camera" && (
                  <ModalPortal>
                    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60">
                      <div className="max-w-2xl w-full mx-4">
                        <CameraCapture
                          onCapture={handleCheckInPhoto}
                          onCancel={handleCancel}
                        />
                      </div>
                    </div>
                  </ModalPortal>
                )}
                {step === "checkin_location" && (
                  <ModalPortal>
                    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60">
                      <div className={`${isDark ? theme.card : "bg-white"} rounded-lg shadow-lg p-6 max-w-md w-full mx-4 border ${theme.border}`}>
                        <LocationAccess
                          onLocation={handleCheckInLocation}
                          onError={handleLocationError}
                          onCancel={handleCancel}
                          submittingMessage={checkInStatus}
                        />
                      </div>
                    </div>
                  </ModalPortal>
                )}
                {step === "checked_in" && checkInAddress && checkInTime && (
                  <div className={`rounded-lg p-6 max-w-2xl mx-auto text-center shadow border ${theme.border} ${theme.card}`}>
                    <h2 className={`text-2xl font-semibold mb-4 ${isDark ? theme.text : "text-black"}`}>
                      Check-In Successful!
                    </h2>
                    {checkInPhoto && (
                      <CheckInImage
                        src={checkInPhoto}
                        alt="Check-in"
                        className={`w-56 h-56 object-cover rounded-lg mx-auto mb-4 border ${theme.border}`}
                        isDark={isDark}
                      />
                    )}
                    <div className={`${isDark ? theme.text : "text-gray-700"} mb-2`}>
                      <strong>Check-In:</strong> {formatDateTime(checkInTime)}
                    </div>
                    <div className={`${isDark ? theme.text : "text-gray-700"} mb-4 whitespace-pre-line`}>
                      <strong>Address:</strong> {checkInAddress}
                    </div>
                    <div className="flex justify-center mt-8">
                      <button
                        onClick={handleCheckOut}
                        className={`flex items-center gap-2 px-6 py-2 rounded-full text-white font-bold text-base shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1 focus:outline-none border-0 ${theme.primaryButton}`}
                        aria-label="Check Out"
                      >
                        <span className="transition-transform duration-200 group-hover:translate-x-1">
                          <ChakraIcon as={FiLogOut} boxSize={5} className="text-white" />
                        </span>
                        <span>Check Out</span>
                      </button>
                    </div>
                  </div>
                )}
                {step === "checkout_camera" && (
                  <ModalPortal>
                    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60">
                      <div className="max-w-2xl w-full mx-4">
                        <CameraCapture
                          onCapture={handleCheckOutPhoto}
                          onCancel={handleCancel}
                        />
                      </div>
                    </div>
                  </ModalPortal>
                )}
                {step === "checkout_location" && (
                  <ModalPortal>
                    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60">
                      <div className={`${isDark ? theme.card : "bg-white"} rounded-lg shadow-lg p-6 max-w-md w-full mx-4 border ${theme.border}`}>
                        <LocationAccess
                          onLocation={handleCheckOutLocation}
                          onError={handleLocationError}
                          onCancel={handleCancel}
                        />
                      </div>
                    </div>
                  </ModalPortal>
                )}
                {step === "summary" &&
                  checkInPhoto &&
                  checkInLocation &&
                  checkInAddress &&
                  checkInTime &&
                  checkOutPhoto &&
                  checkOutLocation &&
                  checkOutAddress &&
                  checkOutTime && (
                    <div className={`rounded-lg p-6 shadow max-w-4xl mx-auto border ${theme.border} ${theme.card}`}>
                      <h2 className={`text-2xl font-semibold text-center mb-6 ${isDark ? theme.text : "text-black"}`}>
                        Check-In/Check-Out Summary
                      </h2>
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Check-In Section */}
                        <div className={`rounded-lg p-4 text-center border ${theme.border} ${isDark ? theme.card : "bg-white"}`}>
                          <h3 className="font-semibold text-lg text-green-700 mb-2">
                            Check-In
                          </h3>
                          <CheckInImage
                            src={checkInPhoto}
                            alt="Check-in"
                            className={`w-40 h-40 object-cover rounded-lg mx-auto mb-3 border ${theme.border}`}
                            isDark={isDark}
                          />
                          <p className={`${isDark ? theme.text : "text-gray-700"} mb-1`}>
                            <strong>Date/Time:</strong>{" "}
                            {formatDateTime(checkInTime)}
                          </p>
                          <p className={`${isDark ? theme.text : "text-gray-700"} whitespace-pre-line`}>
                            <strong>Address:</strong> {checkInAddress}
                          </p>
                        </div>
                        {/* Check-Out Section */}
                        <div className={`rounded-lg p-4 text-center border ${theme.border} ${isDark ? theme.card : "bg-white"}`}>
                          <h3 className="font-semibold text-lg text-green-700 mb-2">
                            Check-Out
                          </h3>
                          <CheckInImage
                            src={checkOutPhoto}
                            alt="Check-out"
                            className={`w-40 h-40 object-cover rounded-lg mx-auto mb-3 border ${theme.border}`}
                            isDark={isDark}
                          />
                          <p className={`${isDark ? theme.text : "text-gray-700"} mb-1`}>
                            <strong>Date/Time:</strong>{" "}
                            {formatDateTime(checkOutTime)}
                          </p>
                          <p className={`${isDark ? theme.text : "text-gray-700"} whitespace-pre-line`}>
                            <strong>Address:</strong> {checkOutAddress}
                          </p>
                        </div>
                      </div>
                      <p className={`text-center font-medium mt-6 ${isDark ? theme.text : "text-gray-700"}`}>
                        <strong>Duration:</strong> {durationStr}
                      </p>
                      {taskUpdateText && (
                        <div className={`mt-4 p-4 rounded-xl border ${isDark ? "bg-[#14161B] border-[#2E2F33]" : "bg-gray-50 border-gray-100"}`}>
                          <h4 className={`text-sm font-bold mb-2 ${isDark ? "text-[#E8C16C]" : "text-gray-800"}`}>
                            Task Detail
                          </h4>
                          <p className={`text-sm italic ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                            "{taskUpdateText}"
                          </p>
                        </div>
                      )}
                      <div className="text-center mt-6">
                        <button
                          onClick={handleCancel}
                          className={`px-8 py-2 rounded-lg font-semibold shadow transition text-white ${theme.primaryButton}`}
                        >
                          New Check-In
                        </button>
                      </div>
                    </div>
                  )}

                {/* Task Update Modal */}
                {showTaskUpdateModal && (
                  <ModalPortal>
                    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in">
                      <div className={`max-w-md w-full mx-4 overflow-hidden rounded-2xl shadow-2xl border transition-all duration-300 transform scale-100 ${isDark ? "bg-[#1A1C1F] border-[#2E2F33]" : "bg-white border-gray-200"}`}>
                        {/* Header */}
                        <div className={`px-6 py-4 border-b flex items-center justify-between ${isDark ? "border-[#2E2F33] bg-[#14161B]" : "border-gray-100 bg-gray-50"}`}>
                          <h3 className={`text-lg font-bold tracking-tight ${isDark ? "text-[#E8C16C]" : "text-gray-800"}`}>
                            Task Detail
                          </h3>
                        </div>

                        {/* Body */}
                        <div className="p-6">
                          <p className={`text-sm mb-4 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                            Please provide a summary of the tasks you completed during this session.
                          </p>
                          <textarea
                            className={`w-full h-32 p-3 text-sm rounded-xl border transition-all duration-200 focus:ring-2 focus:ring-[#B59143] focus:border-transparent resize-none outline-none ${isDark
                              ? "bg-[#0B0B0F] border-[#2E2F33] text-white placeholder-gray-600"
                              : "bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400"
                              }`}
                            placeholder="Type your task detail here..."
                            value={taskUpdateText}
                            onChange={(e) => setTaskUpdateText(e.target.value)}
                          />
                        </div>

                        {/* Footer */}
                        <div className={`px-6 py-4 flex items-center justify-end gap-3 ${isDark ? "bg-[#14161B]" : "bg-gray-50"}`}>
                          <button
                            onClick={() => {
                              setShowTaskUpdateModal(false);
                              setStepSafe("checked_in");
                            }}
                            disabled={isCheckOutSubmitting}
                            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${isDark
                              ? "bg-[#2A3441] text-gray-300 hover:bg-[#3A4553]"
                              : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-100"
                              }`}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleTaskUpdateSubmit}
                            disabled={isCheckOutSubmitting || !taskUpdateText.trim()}
                            className={`flex items-center gap-2 px-6 py-2 rounded-full text-white font-bold text-sm shadow-md transition-all duration-200 transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${theme.primaryButton}`}
                          >
                            {isCheckOutSubmitting ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                <span>Submitting...</span>
                              </>
                            ) : (
                              <span>Submit & Check Out</span>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </ModalPortal>
                )}

                {/* Work Mode Selection Modal */}
                {showWorkModeModal && (
                  <ModalPortal>
                    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in">
                      <div className={`max-w-sm w-full mx-4 overflow-hidden rounded-2xl shadow-2xl border transition-all duration-300 transform scale-100 ${isDark ? "bg-[#1A1C1F] border-[#2E2F33]" : "bg-white border-gray-200"}`}>
                        {/* Header */}
                        <div className={`px-6 py-4 border-b flex items-center justify-between ${isDark ? "border-[#2E2F33] bg-[#14161B]" : "border-gray-100 bg-gray-50"}`}>
                          <h3 className={`text-lg font-bold tracking-tight ${isDark ? "text-[#FBD282]" : "text-gray-800"}`}>
                            Select Work Mode
                          </h3>
                        </div>

                        {/* Body */}
                        <div className="p-8">
                          <div className="flex gap-3 justify-center">
                            {/* Office Mode */}
                            <button
                              onClick={() => setSelectedWorkMode("Office")}
                              className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all duration-200 w-28 ${selectedWorkMode === "Office"
                                ? (isDark ? "border-[#FBD282] bg-[#FBD282]/10" : "border-[#B59143] bg-[#FFF7E6]")
                                : (isDark ? "border-[#2E2F33] bg-transparent" : "border-gray-100 bg-gray-50")
                                }`}
                            >
                              <ChakraIcon as={MdWork} boxSize={6} color={selectedWorkMode === "Office" ? (isDark ? "#FBD282" : "#B59143") : "gray.400"} />
                              <span className={`font-bold text-xs ${selectedWorkMode === "Office" ? (isDark ? "#FBD282" : "#B59143") : "gray.500"}`}>Office</span>
                            </button>

                            {/* Home Mode */}
                            <button
                              onClick={() => setSelectedWorkMode("Home")}
                              className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all duration-200 w-28 ${selectedWorkMode === "Home"
                                ? (isDark ? "border-[#FBD282] bg-[#FBD282]/10" : "border-[#B59143] bg-[#FFF7E6]")
                                : (isDark ? "border-[#2E2F33] bg-transparent" : "border-gray-100 bg-gray-50")
                                }`}
                            >
                              <ChakraIcon as={MdHome} boxSize={6} color={selectedWorkMode === "Home" ? (isDark ? "#FBD282" : "#B59143") : "gray.400"} />
                              <span className={`font-bold text-xs ${selectedWorkMode === "Home" ? (isDark ? "#FBD282" : "#B59143") : "gray.500"}`}>Home</span>
                            </button>
                          </div>
                        </div>

                        {/* Footer */}
                        <div className={`px-6 py-4 flex items-center justify-end gap-3 ${isDark ? "bg-[#14161B]" : "bg-gray-50"}`}>
                          <button
                            onClick={handleWorkModeCancel}
                            disabled={isCheckInSubmitting}
                            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${isDark
                              ? "bg-[#2A3441] text-gray-300 hover:bg-[#3A4553]"
                              : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-100"
                              }`}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleWorkModeSubmit}
                            disabled={isCheckInSubmitting}
                            className={`flex items-center gap-2 px-6 py-1.5 rounded-full text-white font-bold text-xs shadow-md transition-all duration-200 transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${theme.primaryButton}`}
                          >
                            {isCheckInSubmitting ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                <span>Submitting...</span>
                              </>
                            ) : (
                              <span>Confirm</span>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </ModalPortal>
                )}


                {/* spinner handled inside modal button */}
                {error && (
                  <div className="text-center text-red-500 mt-4">{error}</div>
                )}

                {(step === "idle" ||
                  step === "checked_in" ||
                  step === "summary") && (
                    <div className="mt-10">
                      <h3 className={`text-xl font-bold mb-4 text-left ${isDark ? theme.text : "text-black"}`}>
                        History Details :
                      </h3>
                      <div className={`${isDark ? theme.text : "text-black"}`}>
                        <CheckoutComingSoon
                          userId={currentUser?.id || ""}
                          organizationId={organizationId}
                        />
                      </div>
                    </div>
                  )}
              </>
            </>
          ) : localTab === "timesheet" ? (
            <TimeSheetEntry
              organizationId={currentUser?.organizationId || ""}
              userId={currentUser?.id || ""}
              currentUserRole={currentUser?.role}
              currentUser={currentUser}
            />
          ) : localTab === "wfh" ? (
            <WfhApply
              organizationId={currentUser?.organizationId || ""}
              userId={currentUser?.id || ""}
              currentUser={currentUser}
            />
          ) : (
            <LeaveApply
              organizationId={currentUser?.organizationId || ""}
              currentUserRole={currentUser?.role}
              userId={currentUser?.id || ""}
              currentUser={currentUser} />
          )}
        </div>
      </div>

    </div>
  );
};

export default CheckInMain;

// To enable the sun icon glow, add this to your global CSS or Tailwind config:
// .shadow-inner-glow { box-shadow: 0 0 32px 8px #fde68a, 0 0 0 4px #fef3c7; }
